import { POLICY_VERSION, type GeoScope, type StoredConsent } from './consentStore';

/**
 * Pure decision helpers for the consent flow — no React, no I/O, so the whole matrix is unit-testable
 * (geo scope × decided/undecided × version match). Consumed by the Hotjar gate (F2) and the banner (F3).
 */

/** A stored decision counts only if it was made against the CURRENT policy version (else we re-prompt). */
export function hasValidConsent(consent: StoredConsent | null, version: string = POLICY_VERSION): boolean {
  return consent != null && consent.version === version;
}

/**
 * Show the consent banner ONLY to in-scope (EU/EEA) users who have not decided under the current version.
 * A prior valid decision ⇒ never re-ask. `pending`/`out-of-scope` ⇒ no banner.
 */
export function shouldShowBanner(
  scope: GeoScope, consent: StoredConsent | null, version: string = POLICY_VERSION,
): boolean {
  if (hasValidConsent(consent, version)) return false;
  return scope === 'in-scope';
}

/**
 * Whether non-essential analytics (Hotjar) may load right now:
 *  - a valid stored decision wins (its `analytics` flag), regardless of scope;
 *  - otherwise: `out-of-scope` ⇒ allowed; `pending` ⇒ held (still resolving); `in-scope` + undecided ⇒ held.
 * "Held" (false) is why Hotjar waits for the async geo result instead of loading eagerly for EU visitors.
 */
export function analyticsAllowed(
  scope: GeoScope, consent: StoredConsent | null, version: string = POLICY_VERSION,
): boolean {
  if (hasValidConsent(consent, version)) return consent!.analytics;
  return scope === 'out-of-scope';
}
